import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Circle,
  ExternalLink,
  Copy,
  Ticket,
} from "lucide-react";
import { toast } from "sonner";
import { fetcher } from "@/lib/api";
import "@/i18n/transparencia_integracion";

const STATUS_COLORS = {
  healthy: { text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500" },
  degraded: { text: "text-amber-800", bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500" },
  down: { text: "text-red-700", bg: "bg-red-50", border: "border-red-200", dot: "bg-red-500" },
  unknown: { text: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200", dot: "bg-slate-400" },
};

const ESG_STATUS_COLORS = {
  active: STATUS_COLORS.healthy,
  provisional: STATUS_COLORS.degraded,
  blocked: { text: "text-slate-700", bg: "bg-slate-50", border: "border-slate-300", dot: "bg-slate-500" },
};

function StatusPill({ status, label, testid }) {
  const style = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.14em] ${style.bg} ${style.border} ${style.text}`}
      data-testid={testid}
    >
      <i className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {label}
    </span>
  );
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).then(
    () => toast.success("Copiado ✓"),
    () => toast.error("Non se puido copiar"),
  );
}

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "es" ? "es-ES" : "gl-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function IntegrationStatus() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data, error } = useSWR("/public/integration-status", fetcher, {
    revalidateOnFocus: false,
    refreshInterval: 5 * 60 * 1000,
  });

  if (error) return null;
  if (!data) {
    return (
      <div className="rounded-2xl border hairline bg-white p-8 text-sm text-salnes-stone" data-testid="transparencia-integration-status-loading">
        …
      </div>
    );
  }

  const s = data.summary || {};
  const integrations = data.integrations || [];
  const blockers = data.active_blockers || [];
  const esgState = data.esg_indicators_state || [];

  return (
    <section
      className="rounded-2xl border hairline bg-white overflow-hidden"
      data-testid="transparencia-integration-status"
    >
      {/* Header */}
      <div className="px-6 py-6 lg:px-8 lg:py-8 border-b hairline bg-salnes-sand/20">
        <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-2">
          {t("transparencia_integracion.kicker")}
        </div>
        <h2 className="font-serif text-3xl lg:text-4xl text-salnes-ink leading-tight">
          {t("transparencia_integracion.title")}
        </h2>
        <p className="mt-3 text-sm lg:text-base text-salnes-ink/75 max-w-3xl leading-relaxed">
          {t("transparencia_integracion.principle")}
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-6 lg:px-8">
        <SummaryTile
          label={t("transparencia_integracion.tile_esg_active")}
          value={s.esg_kpis_active}
          denominator={s.esg_kpis_total}
          accent="text-salnes-emerald"
          testid="tile-esg-active"
        />
        <SummaryTile
          label={t("transparencia_integracion.tile_esg_provisional")}
          value={s.esg_kpis_provisional || 0}
          denominator={s.esg_kpis_total}
          accent="text-amber-700"
          testid="tile-esg-provisional"
        />
        <SummaryTile
          label={t("transparencia_integracion.tile_integrations_healthy")}
          value={s.integrations_healthy}
          denominator={s.integrations_total}
          accent="text-salnes-atlantic"
          testid="tile-integrations-healthy"
        />
        <SummaryTile
          label={t("transparencia_integracion.tile_blockers")}
          value={s.active_blockers_count}
          accent="text-salnes-coral"
          testid="tile-blockers"
        />
      </div>

      {/* Integrations table */}
      <div className="px-6 lg:px-8 pb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone mb-3">
          {t("transparencia_integracion.integrations_title")}
        </div>
        <div className="rounded-xl border hairline overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-salnes-sand/40 text-salnes-stone">
              <tr>
                <th className="text-left px-3 py-2 font-medium">{t("transparencia_integracion.th_integration")}</th>
                <th className="text-left px-3 py-2 font-medium">{t("transparencia_integracion.th_provider")}</th>
                <th className="text-left px-3 py-2 font-medium">{t("transparencia_integracion.th_status")}</th>
                <th className="text-right px-3 py-2 font-medium">{t("transparencia_integracion.th_endpoints")}</th>
                <th className="text-left px-3 py-2 font-medium hidden lg:table-cell">{t("transparencia_integracion.th_last_check")}</th>
              </tr>
            </thead>
            <tbody>
              {integrations.map((i) => (
                <tr
                  key={i.code}
                  className="border-t hairline"
                  data-testid={`integration-tile-${i.code}`}
                >
                  <td className="px-3 py-3">
                    <div className="font-medium text-salnes-ink">
                      {lang === "es" ? i.name_es : i.name_gl}
                    </div>
                    <div className="text-[10px] text-salnes-stone leading-tight mt-0.5">
                      {lang === "es" ? i.description_es : i.description_gl}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-salnes-ink/80">{i.provider}</td>
                  <td className="px-3 py-3">
                    <StatusPill
                      status={i.status}
                      label={t(`transparencia_integracion.status_${i.status}`)}
                      testid={`integration-status-${i.code}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    <span className="text-salnes-ink">{i.endpoints_ok}</span>
                    <span className="text-salnes-stone">/{i.endpoints_total}</span>
                  </td>
                  <td className="px-3 py-3 text-salnes-stone tabular-nums hidden lg:table-cell">
                    {formatDate(i.last_check_at, lang)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active blockers */}
      <div className="px-6 lg:px-8 pb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone mb-3 flex items-center gap-2">
          <Ticket size={12} strokeWidth={1.6} />
          {t("transparencia_integracion.blockers_title")}
        </div>
        {blockers.length === 0 ? (
          <div
            className="rounded-xl border hairline bg-emerald-50/50 border-emerald-200 px-4 py-6 text-sm text-emerald-800 text-center"
            data-testid="blockers-empty"
          >
            <CheckCircle2 size={16} className="inline mr-2" />
            {t("transparencia_integracion.blockers_empty")}
          </div>
        ) : (
          <div className="space-y-3">
            {blockers.map((b) => (
              <BlockerCard key={b.id} blocker={b} lang={lang} />
            ))}
          </div>
        )}
      </div>

      {/* ESG state grid */}
      <div className="px-6 lg:px-8 pb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone mb-3">
          {t("transparencia_integracion.esg_title")}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {esgState.map((e, idx) => (
            <EsgMiniCard key={e.code} esg={e} lang={lang} index={idx} />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t hairline px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-2 text-xs text-salnes-stone">
        <div className="tabular-nums">
          {t("transparencia_integracion.footer_updated")}:{" "}
          <span data-testid="transparencia-updated-at">
            {formatDate(data.computed_at, lang)}
          </span>
        </div>
        <Link
          to="/datos-abertos"
          className="inline-flex items-center gap-1.5 text-salnes-atlantic hover:underline"
          data-testid="transparencia-link-datos-abertos"
        >
          {t("transparencia_integracion.footer_api")}
          <ExternalLink size={11} />
        </Link>
      </div>
    </section>
  );
}

function SummaryTile({ label, value, denominator, accent, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-xl border hairline bg-white p-5"
      data-testid={testid}
    >
      <div className={`text-[10px] uppercase tracking-[0.22em] ${accent}`}>{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-serif text-4xl text-salnes-ink tabular-nums leading-none">
          {value ?? "—"}
        </div>
        {denominator != null && (
          <div className="text-xs text-salnes-stone tabular-nums">
            / {denominator}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function BlockerCard({ blocker, lang }) {
  const { t } = useTranslation();
  const title = lang === "es" ? blocker.title_es : blocker.title_gl;
  const desc = lang === "es" ? blocker.description_es : blocker.description_gl;
  const statusKey = `transparencia_integracion.blocker_status_${blocker.status}`;
  const statusLabel = t(statusKey, {
    defaultValue: blocker.status,
  });
  return (
    <div
      className="rounded-xl border hairline bg-amber-50/40 border-amber-200 p-4"
      data-testid={`blocker-${blocker.id}`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-serif text-lg text-salnes-ink leading-tight">{title}</h4>
            <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
              {statusLabel}
            </span>
          </div>
          <p className="mt-2 text-sm text-salnes-ink/80 leading-relaxed">{desc}</p>
          <dl className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[11px]">
            <div>
              <dt className="inline text-salnes-stone">{t("transparencia_integracion.blocker_provider")}:</dt>{" "}
              <dd className="inline text-salnes-ink/80">{blocker.provider}</dd>
            </div>
            <div>
              <dt className="inline text-salnes-stone">{t("transparencia_integracion.blocker_detected")}:</dt>{" "}
              <dd className="inline text-salnes-ink/80 tabular-nums">
                {formatDate(blocker.detected_at, lang)}
              </dd>
            </div>
            {blocker.trace_id && (
              <div className="md:col-span-2">
                <dt className="inline text-salnes-stone">{t("transparencia_integracion.blocker_traceid")}:</dt>{" "}
                <dd className="inline">
                  <code
                    onDoubleClick={() => copyToClipboard(blocker.trace_id)}
                    className="cursor-pointer text-[10px] px-1.5 py-0.5 rounded bg-white border hairline text-salnes-ink tabular-nums select-all break-all"
                    title={t("transparencia_integracion.blocker_traceid_copy")}
                    data-testid={`blocker-traceid-${blocker.id}`}
                  >
                    {blocker.trace_id}
                  </code>
                  <button
                    onClick={() => copyToClipboard(blocker.trace_id)}
                    className="ml-1.5 text-salnes-stone hover:text-salnes-atlantic transition"
                    title={t("transparencia_integracion.blocker_traceid_copy")}
                    data-testid={`blocker-copy-${blocker.id}`}
                  >
                    <Copy size={10} />
                  </button>
                </dd>
              </div>
            )}
            {blocker.impact_kpis?.length > 0 && (
              <div className="md:col-span-2">
                <dt className="inline text-salnes-stone">{t("transparencia_integracion.blocker_impact")}:</dt>{" "}
                <dd className="inline text-salnes-ink/80">
                  {blocker.impact_kpis.map((k) => (
                    <code
                      key={k}
                      className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-white border hairline text-salnes-ink mr-1 mb-1"
                    >
                      {k}
                    </code>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}

function EsgMiniCard({ esg, lang, index }) {
  const { t } = useTranslation();
  const name = lang === "es" ? esg.name_es : esg.name_gl;
  const style = ESG_STATUS_COLORS[esg.status] || ESG_STATUS_COLORS.blocked;
  const isBlocked = esg.status === "blocked";
  const isProvisional = esg.status === "provisional";
  const reason = lang === "es"
    ? (isProvisional ? esg.provisional_reason_es : esg.blocked_reason_es)
    : (isProvisional ? esg.provisional_reason_gl : esg.blocked_reason_gl);
  const badge =
    esg.status === "active"
      ? t("transparencia_integracion.esg_active_badge")
      : esg.status === "blocked"
        ? t("transparencia_integracion.esg_blocked_badge")
        : t("transparencia_integracion.esg_provisional_badge");
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.03 }}
      className={`rounded-xl border p-3.5 ${style.bg} ${style.border} min-h-[110px] flex flex-col justify-between`}
      data-testid={`esg-state-${esg.code}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-medium text-salnes-ink leading-tight line-clamp-2">
          {name}
        </div>
        <span className={`text-[9px] uppercase tracking-[0.12em] font-medium ${style.text}`}>
          {badge}
        </span>
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="font-serif text-2xl text-salnes-ink tabular-nums leading-none">
          {esg.value != null ? esg.value : "—"}
        </div>
        <div className="text-[10px] text-salnes-stone">{esg.unit}</div>
      </div>
      {(isBlocked || isProvisional) && reason && (
        <div
          className="mt-2 text-[9px] uppercase tracking-[0.1em] text-slate-600"
          data-testid={`esg-state-reason-${esg.code}`}
        >
          {reason}
        </div>
      )}
    </motion.div>
  );
}
